"use client";

import FeedbackModal from "@/components/FeedbackModal";
import { ExternalLink } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

const footerLinks = {
  Product: [
    { name: "Features", href: "/landing#features" },
    { name: "Pricing", href: "/landing#pricing" },
    { name: "Languages", href: "/landing/languages" },
    { name: "Language Tutors", href: "/tutor" },
    { name: "AI Tutors", href: "/ai" },
    { name: "Speaking Practice", href: "/practice" },
    { name: "Hybrid School", href: "/school" },
    { name: "Teach with Us", href: "/teach" },
    { name: "For Tutors", href: "/tutors" },
    { name: "Schools", href: "/schools" },
    { name: "Enterprise", href: "/enterprise" },
    { name: "Corporate", href: "/corporate" },
    { name: "Learn", href: "/learn" },
    { name: "Tools", href: "/tools" },
    { name: "Blog", href: "/blog" },
  ],
  Resources: [
    { name: "Writing Practice", href: "/tools/writing-practice-language-learning" },
    { name: "Fill-in-the-Gap", href: "/tools/fill-in-the-gap-language-practice" },
    { name: "About", href: "/about" },
    { name: "Support", href: "/support" },
    { name: "Contact", href: "/contact" },
    { name: "Feedback", href: "#feedback" },
    { name: "Affiliate Program", href: "/affiliate" },
    { name: "For Creators", href: "/influencer" },
  ],
  Legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
  ],
};

export function LandingFooter({ variant = "light" }: { variant?: "light" | "dark" }) {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const isDark = variant === "dark";

  const headingClassName = isDark ? "text-white" : "text-gray-900";
  const linkClassName = isDark
    ? "text-gray-400 hover:text-white"
    : "text-gray-500 hover:text-gray-900";

  return (
    <footer
      className={
        isDark ? "border-t border-white/10 bg-[#0F0F14]" : "border-t-2 border-gray-200 bg-white"
      }
    >
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-16 lg:px-8">
        <div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <Link href="/" className="mb-4 flex items-center gap-2">
              <Image src="/ui/Chickylogovector.gif" alt="ChickyTutor" width={32} height={32} />
              <span className={`text-lg font-bold ${headingClassName}`}>ChickyTutor</span>
            </Link>
            <p className={`mb-4 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              AI language tutor for speaking practice in 70+ languages.
            </p>
            <a
              href="https://linkedin.com/company/chickytutor"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex transition-colors ${
                isDark ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-600"
              }`}
              aria-label="ChickyTutor on LinkedIn"
            >
              <ExternalLink className="h-5 w-5" aria-hidden="true" />
            </a>
          </div>

          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className={`mb-4 font-semibold ${headingClassName}`}>{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    {link.name === "Feedback" ? (
                      <button
                        type="button"
                        onClick={() => setIsFeedbackOpen(true)}
                        className={`text-left text-sm transition-colors ${linkClassName}`}
                      >
                        {link.name}
                      </button>
                    ) : (
                      <Link
                        href={link.href}
                        className={`text-sm transition-colors ${linkClassName}`}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className={`flex flex-col items-center justify-between gap-4 border-t pt-8 md:flex-row ${
            isDark ? "border-white/10" : "border-gray-200"
          }`}
        >
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            © {new Date().getFullYear()} ChickyTutor. All rights reserved.
          </p>
          <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            Built for language learners everywhere.
          </p>
        </div>
      </div>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </footer>
  );
}
